import React, { useCallback, useState } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  ConnectionLineType,
} from 'reactflow';
import CustomNode, { CustomNodeData } from './FlowchartCustom.tsx';

const nodeTypes = {
  custom: CustomNode,
};

interface NodeConfig {
  id: string;
  label: string;
  variant: 'primary' | 'secondary' | 'root';
  iconName?: string;
  children?: NodeConfig[];
  type?: string;
  dec?: string;
}



const generateNodesAndEdges = (
  data: NodeConfig[],
  expandedNodes: Set<string>,
  onToggle: (id: string) => void,
  parentId?: string,
  level = 0,
  yOffset = 0
): { nodes: Node<CustomNodeData>[]; edges: Edge[]; maxY: number } => {
  const nodes: Node<CustomNodeData>[] = [];
  const edges: Edge[] = [];
  let currentY = yOffset;

  data.forEach((item, index) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedNodes.has(item.id);
    const x = level * 400;
    const y = currentY;

    nodes.push({
      id: item.id,
      type: 'custom',
      position: { x, y },
      data: {
        label: item.label,
        variant: item.variant,
        isExpanded,
        hasChildren,
        onToggle: hasChildren ? () => onToggle(item.id) : undefined,
        iconName: item.iconName,
        type: item.type,
        dec: item.dec,
      },
    });

    if (parentId) {
      const parentIsExpanded = expandedNodes.has(parentId);
      edges.push({
        id: `${parentId}-${item.id}`,
        source: parentId,
        target: item.id,
        type: ConnectionLineType.SmoothStep,
        style: { 
          stroke: parentIsExpanded ? 'hsl(var(--connector-active))' : 'hsl(var(--connector))', 
          strokeWidth: parentIsExpanded ? 3 : 2 
        },
        animated: parentIsExpanded,
      });
    }

    currentY += 180;

    if (hasChildren && isExpanded && item.children) {
      const childResult = generateNodesAndEdges(
        item.children,
        expandedNodes,
        onToggle,
        item.id,
        level + 1,
        y + 100
      );
      nodes.push(...childResult.nodes);
      edges.push(...childResult.edges);
      currentY = Math.max(currentY, childResult.maxY);
    }
  });

  return { nodes, edges, maxY: currentY };
};

export const Flowchart = ({graphData}) => {
  const flowData: NodeConfig[] = graphData;
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(
    new Set([''])
  );

  const handleToggle = useCallback((nodeId: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  const { nodes: initialNodes, edges: initialEdges } = generateNodesAndEdges(
    flowData,
    expandedNodes,
    handleToggle
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes and edges when expandedNodes changes
  React.useEffect(() => {
    const updatedResult = generateNodesAndEdges(flowData, expandedNodes, handleToggle);
    setNodes(updatedResult.nodes);
    setEdges(updatedResult.edges);
  }, [expandedNodes, handleToggle, setNodes, setEdges]);
  
  return (
    <div className="w-full h-screen bg-background">     
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        nodesDraggable={true}
        fitView = { false }
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.2}
        maxZoom={2.0}
        defaultViewport={{ x: 40, y: 40, zoom: 1 }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#bb1ccc" />
        <Controls className="!absolute !bottom-[175px] !left-4 bg-card border border-border z-50" />
        </ReactFlow>
    </div>
  );
};